import { shallowRef, onBeforeUnmount, reactive, unref, watch, isRef } from 'vue';
import type { MaybeRef } from 'vue';
import { cloneDeep } from 'lodash-es';
import type { colorMap } from '@/types/tags';

interface UsePageGroupTitleState {
  title: string;
  tagText?: string;
  tagColor?: keyof typeof colorMap;
  subtitle?: string;
  subtitleProps?: Record<string, unknown>;
  headerRightProps?: Record<string, unknown>;
}

const DEFAULT_STATE: UsePageGroupTitleState = {
  title: '',
  tagText: undefined,
  tagColor: undefined,
  subtitle: undefined,
  subtitleProps: undefined,
} as const;

const getDefaultState = () => cloneDeep(DEFAULT_STATE);

const state = reactive<UsePageGroupTitleState>(getDefaultState());
const subtitleComponent = shallowRef<string | object | undefined>(undefined); // Track subtitle component separately
const headerRightComponent = shallowRef<string | object | undefined>(undefined); // Track header right component separately

function resetState() {
  Object.assign(state, getDefaultState());
  subtitleComponent.value = undefined;
}

export function usePageGroupTitle(options: {
  title?: MaybeRef<string | undefined>;
  tagText?: MaybeRef<string | undefined>;
  tagColor?: MaybeRef<keyof typeof colorMap | undefined>;
  subtitle?: MaybeRef<string | undefined>;
  subtitleComponentInitial?: MaybeRef<string | object | undefined>;
  subtitleProps?: MaybeRef<Record<string, unknown> | undefined>;
  headerRightComponentInitial?: MaybeRef<string | object | undefined>;
  headerRightProps?: MaybeRef<Record<string, unknown> | undefined>;
} = {}) {
  const {
    title,
    tagText,
    tagColor,
    subtitle,
    subtitleComponentInitial,
    subtitleProps,
    headerRightComponentInitial,
    headerRightProps,
  } = options;

  const setStateField = <K extends keyof UsePageGroupTitleState>(
    field: K,
    value: MaybeRef<UsePageGroupTitleState[K] | undefined>,
  ) => {
    if (isRef(value)) {
      watch(
        value,
        (val) => {
          state[field] = (unref(val) ?? DEFAULT_STATE[field]) as UsePageGroupTitleState[K];
        },
        { immediate: true },
      );
    } else if (value !== undefined) {
      state[field] = value as UsePageGroupTitleState[K];
    }
  };

  const setTitle = (val: string) => (state.title = val);
  const setTagText = (val?: string) => (state.tagText = val);
  const setTagColor = (val?: keyof typeof colorMap) => (state.tagColor = val);
  const setSubtitle = (val?: string) => (state.subtitle = val);
  const setSubtitleComponent = (val?: string | object) => {
    subtitleComponent.value = val; // Directly update the shallowRef
  };
  const setSubtitleProps = (props: Record<string, unknown>) => (state.subtitleProps = props);
  const setHeaderRightComponent = (val?: string | object) => {
    headerRightComponent.value = val; // Directly update the shallowRef
  };
  const setHeaderRightProps = (props: Record<string, unknown>) => (state.headerRightProps = props);
  setStateField('title', title);
  setStateField('tagText', tagText);
  setStateField('tagColor', tagColor);
  setStateField('subtitle', subtitle);
  setSubtitleComponent(subtitleComponentInitial);
  setStateField('subtitleProps', subtitleProps);
  setHeaderRightComponent(headerRightComponentInitial);
  setStateField('headerRightProps', headerRightProps);

  onBeforeUnmount(() => resetState());

  return {
    state,
    subtitleComponent,
    headerRightComponent,
    setTitle,
    setTagText,
    setTagColor,
    setSubtitle,
    setSubtitleComponent,
    setSubtitleProps,
    setHeaderRightComponent,
    setHeaderRightProps,
  };
}
